/**
 * Strict grade identity for features that persist a class against a curriculum node.
 *
 * A class name is the primary identity (G5A, Grade 5 A). Explicit notation starts at the beginning
 * or after a character outside ASCII A–Z/0–9, then G or Grade, ASCII whitespace, and the
 * canonical one/two-digit number 1–10 without a leading zero. Exam bands such as Movers, Starters
 * and PET are not grades. The level is only fallback evidence when it uses that notation.
 * Conflicting, repeated-different, long and out-of-department evidence is refused rather than
 * guessed. Keep main_0033's trigger matrix aligned with this function.
 */
// SQL's ltrim grammar in main_0033 uses this same explicit ASCII whitespace set. NBSP and other
// Unicode spacing are not silently normalized into class identity.
const EXPLICIT_GRADE = /(?:^|[^A-Za-z0-9])g(?:rade)?[ \t\r\n\f\v]*([0-9]+)/gi;

function evidence(value: string): { grades: number[]; invalid: boolean } {
  const grades: number[] = [];
  let invalid = false;
  for (const match of value.matchAll(EXPLICIT_GRADE)) {
    const digits = match[1]!;
    const grade = Number(digits);
    if (digits.length > 2 || digits.startsWith("0") || !Number.isSafeInteger(grade) || grade < 1 || grade > 10) invalid = true;
    else if (!grades.includes(grade)) grades.push(grade);
  }
  return { grades, invalid };
}

export function departmentGradeFromClass(cls: { name: string; level: string | null }): number | null {
  const name = evidence(cls.name);
  const level = evidence(cls.level ?? "");
  if (name.invalid || level.invalid || name.grades.length > 1 || level.grades.length > 1) return null;
  const named = name.grades[0] ?? null;
  const explicitLevel = level.grades[0] ?? null;
  if (named !== null && explicitLevel !== null && named !== explicitLevel) return null;
  return named ?? explicitLevel;
}
